import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'
import { invalidateFor } from '@/lib/query-keys'
import { DsOverlay, DsToast } from '@/ds'
import { useIsLandlord, useUser } from '@/features/auth/useSession'
import { APP_FEATURES } from '@/features/flags/flags.store'
import { useFeature } from '@/features/flags/useFeature'
import { leaseApi } from '@/features/lease/lease.api'
import { leaseKeys } from '@/features/lease/useLeases'
import { rentCheckoutFlow } from '@/features/payments/flows'
import { isUpiDirect, paymentsApi, type PayRentBody } from '@/features/payments/payments.api'
import { groupAvailability, isOnlineGroup, isPayableLease, leaseDueAmount } from '@/features/payments/pay-rent-utils'
import { payRentStrings as s } from '@/features/payments/pay-rent-strings'
import { useCheckout } from '@/features/payments/useCheckout'
import { UpiDirectSheet } from '@/features/payments/UpiDirectSheet'
import type { Lease, PaymentGroup, PaymentGroupKey, UpiDirectResponse } from '@/types/models'
import { readRecordCapabilities } from '@/features/capabilities/capabilities'

export type PayStage = 'lease' | 'form' | 'success'

export interface PaymentResult {
  transactionId: string
  amount: number
  group: PaymentGroupKey
  lease: Lease
  paidAt: Date
  /** False while the money waits on the landlord (a tenant's cash or UPI claim, an unconfirmed online payment): no receipt yet (LP-695). */
  settled: boolean
}

export function usePayRent(initialLeaseId: string | undefined, initialAmount: number | null) {
  const user = useUser()
  const isLandlord = useIsLandlord()
  const [leaseId, setLeaseId] = useState<string | undefined>(initialLeaseId)
  const [stage, setStage] = useState<PayStage>(initialLeaseId ? 'form' : 'lease')
  const [amount, setAmount] = useState<number | null>(initialAmount)
  const [group, setGroup] = useState<PaymentGroupKey>('offline')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<PaymentResult | null>(null)
  // The backend's refusal, shown in the form beside the submit (LP-651); cleared once the amount, method or lease changes.
  const [error, setError] = useState<string | null>(null)
  useEffect(() => setError(null), [amount, group, leaseId])

  const leases = useQuery({ queryKey: leaseKeys.list({ dropdown: true, size: 250 }), queryFn: () => leaseApi.list({ dropdown: true, size: 250, page: 1 }), enabled: stage === 'lease' })
  const lease = useQuery({ queryKey: leaseKeys.detail(leaseId ?? ''), queryFn: () => leaseApi.get(leaseId!), enabled: !!leaseId })
  useEffect(() => {
    if (lease.data && amount == null) setAmount(lease.data.rent)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lease.data])

  const groups = useQuery({ queryKey: ['payments', 'groups'], queryFn: paymentsApi.groups, enabled: !isLandlord, staleTime: 60 * 60 * 1000 })
  const banking = useQuery({ queryKey: ['payments', 'banking-status', leaseId], queryFn: () => paymentsApi.bankingStatus(leaseId!), enabled: !isLandlord && !!leaseId })
  const online = useFeature(APP_FEATURES.onlinePayment)
  const upi = useFeature(APP_FEATURES.upiDirect)
  // Read from the lease, not the session: the backend resolves the landlord's and the property's country (incl. cross-border payout) onto the lease.
  // A lease without that block reads as allowed, the right fallback while backend and client deploy out of step.
  const paymentsAllowed = useMemo(() => readRecordCapabilities(lease.data).payments, [lease.data])
  const availability = useMemo(
    () => Object.fromEntries((groups.data ?? []).map((g) => [g.key, groupAvailability(g, { online, upi }, banking.data, paymentsAllowed)])) as Record<PaymentGroupKey, ReturnType<typeof groupAvailability>>,
    [groups.data, online, upi, banking.data, paymentsAllowed],
  )
  useEffect(() => {
    if (group !== 'offline' && availability[group] && !availability[group].enabled) setGroup('offline')
  }, [availability, group])

  // Fee estimate for online groups, 600 ms after the amount settles.
  const [debounced, setDebounced] = useState(amount)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(amount), 600)
    return () => clearTimeout(t)
  }, [amount])
  const fee = useQuery({
    queryKey: ['payments', 'fee-estimate', leaseId, debounced, group],
    queryFn: () => paymentsApi.feeEstimate(leaseId!, debounced!, group, lease.data?.propertyType),
    enabled: !isLandlord && !!leaseId && !!debounced && debounced > 0 && isOnlineGroup(group),
    staleTime: 10 * 60 * 1000,
  })

  const due = leaseDueAmount(lease.data)
  // An ended or not-yet-active lease takes no payment (LP-651): the form is replaced by a line saying so.
  const blocked = !!lease.data && !isPayableLease(lease.data)
  const valid = !!lease.data && !blocked && (amount ?? 0) > 0 && (!isOnlineGroup(group) || (!!fee.data && debounced === amount))

  const selectGroup = (g: PaymentGroup) => {
    const a = availability[g.key]
    if (a && !a.enabled) {
      if (a.reason) DsToast.show(a.reason, { tone: 'warning' })
      return
    }
    setGroup(g.key)
  }

  const bodyRef = useRef<PayRentBody & { feeToken: string }>({ leaseId: '', amount: 0, group: 'standard', feeToken: '' })
  // The flow keeps a reference to `bodyRef.current`; submit fills it in before running.
  const checkout = useCheckout(rentCheckoutFlow(bodyRef.current, lease.data?.currency))

  const finish = (transactionId: string, settled: boolean) => {
    setResult({ transactionId, amount: amount ?? 0, group, lease: lease.data!, paidAt: new Date(), settled })
    setStage('success')
    void invalidateFor('payment')
  }

  const submit = async () => {
    if (!lease.data || !amount || blocked) return
    setSubmitting(true)
    setError(null)
    try {
      const body: PayRentBody = { leaseId: lease.data.id, amount, group: isLandlord ? 'offline' : group, ...(note.trim() ? { note: note.trim() } : {}) }
      if (isOnlineGroup(body.group)) {
        if (!fee.data) throw new ApiError(0, s.somethingWentWrong)
        Object.assign(bodyRef.current, { ...body, feeToken: fee.data.feeToken })
        const phase = await checkout.run()
        if (phase === 'success' || phase === 'processing' || phase === 'unconfirmed') finish('online', phase === 'success')
        else if (phase === 'failed') DsToast.error(checkout.error ?? s.paymentFailed)
        return
      }
      const res = await paymentsApi.payRent(body)
      // A landlord's record is settled at once; a tenant's cash or UPI claim waits for the landlord to accept it.
      if (typeof res === 'string') return finish(res, isLandlord)
      if (isUpiDirect(res)) {
        const outcome = await runUpiDirect(res)
        if (outcome) finish(res.transactionId, false)
        return
      }
      finish(res.transactionId, isLandlord)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : s.somethingWentWrong)
    } finally {
      setSubmitting(false)
    }
  }

  /** Sheet with the QR/intent; resolves true after confirm-upi, false after cancel. */
  const runUpiDirect = (order: UpiDirectResponse) =>
    DsOverlay.sheet<boolean>({
      title: s.upiTitle,
      closeLabel: s.cancel,
      isDismissible: false,
      size: 'lg',
      builder: (close) => (
        <UpiDirectSheet
          order={order}
          onPaid={async () => {
            try {
              await paymentsApi.confirmUpi(order.transactionId)
              close(true)
            } catch (e) {
              DsToast.error(e instanceof ApiError ? e.message : s.somethingWentWrong)
            }
          }}
          onCancel={async () => {
            await paymentsApi.cancel(order.transactionId).catch(() => undefined)
            close(false)
          }}
        />
      ),
    }).then((r) => r === true)

  const reset = () => {
    setResult(null)
    setLeaseId(undefined)
    setAmount(null)
    setGroup('offline')
    setNote('')
    setStage('lease')
  }

  return {
    user,
    isLandlord,
    stage,
    setStage,
    leases: leases.data?.items ?? [],
    leasesLoading: leases.isPending && stage === 'lease',
    leasesError: leases.isError ? leases.error : null,
    refetchLeases: leases.refetch,
    pickLease: (l: Lease) => {
      setLeaseId(l.id)
      setAmount(l.rent)
      setStage('form')
    },
    lease: lease.data,
    leaseLoading: !!leaseId && lease.isPending,
    leaseError: lease.isError ? lease.error : null,
    refetchLease: lease.refetch,
    due,
    blocked,
    error,
    amount,
    setAmount,
    group,
    selectGroup,
    groups: groups.data ?? [],
    availability,
    paymentsAllowed,
    note,
    setNote,
    fee: fee.data ?? null,
    feeLoading: isOnlineGroup(group) && (fee.isFetching || debounced !== amount),
    valid,
    submitting: submitting || checkout.isBusy,
    checkout,
    submit,
    result,
    reset,
  }
}

export type PayRentVm = ReturnType<typeof usePayRent>
